import React, { useRef, useEffect } from 'react';
import './DraggableSeparator.css';

const DraggableSeparator = ({
  onResize,
  minWidth = 200,
  maxWidth = null,
  side = 'right',
  orientation = 'vertical',
  currentSize,
  minHeight = 100,
  maxHeight = null,
  onDragStart,
  onDragEnd,
  style,
  className,
  collapseButton = null,
}) => {
  const separatorRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  // Keep callbacks and currentSize in refs so effect doesn't re-run when parent re-renders during drag
  const onResizeRef = useRef(onResize);
  const onDragStartRef = useRef(onDragStart);
  const onDragEndRef = useRef(onDragEnd);
  const currentSizeRef = useRef(currentSize);
  onResizeRef.current = onResize;
  onDragStartRef.current = onDragStart;
  onDragEndRef.current = onDragEnd;
  currentSizeRef.current = currentSize;

  useEffect(() => {
    const separator = separatorRef.current;
    if (!separator) return;

    const handleMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      isDraggingRef.current = true;
      separator.classList.add('dragging');
      if (orientation === 'vertical') {
        document.body.style.cursor = 'col-resize';
      } else {
        document.body.style.cursor = 'row-resize';
        startYRef.current = e.clientY;
        startHeightRef.current = typeof currentSizeRef.current === 'number' ? currentSizeRef.current : 0;
      }
      document.body.style.userSelect = 'none';
      onDragStartRef.current?.();
    };

    const handleMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();

      if (orientation === 'vertical') {
        const container = separator.parentElement;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const sidebarWidth = side === 'right' ? rect.width - mouseX : mouseX;
        const chartMinWidth = 320;
        const maxAllowed = rect.width - chartMinWidth;
        const effectiveMax = maxWidth != null ? Math.min(maxWidth, maxAllowed) : maxAllowed;
        const w = Math.max(minWidth, Math.min(effectiveMax, sidebarWidth));
        onResizeRef.current?.(w);
      } else {
        const deltaY = startYRef.current - e.clientY;
        const rawHeight = startHeightRef.current + deltaY;
        const viewportMax = Math.floor(window.innerHeight * 0.8);
        const effectiveMax = maxHeight != null ? Math.min(maxHeight, viewportMax) : viewportMax;
        const h = Math.max(minHeight, Math.min(effectiveMax, rawHeight));
        onResizeRef.current?.(h);
      }
    };

    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      separator.classList.remove('dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      onDragEndRef.current?.();
    };

    separator.addEventListener('mousedown', handleMouseDown);

    // Attach move/up to document so we get them even if mouse leaves separator
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      separator.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [orientation, side, minWidth, maxWidth, minHeight, maxHeight]);

  const orientationClass = orientation === 'horizontal' ? 'horizontal' : 'vertical';
  const buttonStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 11,
    pointerEvents: 'auto',
    ...(orientation === 'vertical' && (side === 'left' ? { left: 3 } : { right: 3 })),
  };

  return (
    <div
      ref={separatorRef}
      className={`draggable-separator ${orientationClass}${className ? ` ${className}` : ''}`}
      title="Drag to resize"
      style={{ position: 'relative', ...style }}
    >
      {collapseButton && (
        <div style={buttonStyle} onMouseDown={(e) => e.stopPropagation()}>
          {collapseButton}
        </div>
      )}
    </div>
  );
};

export default DraggableSeparator;
